import React, { useId } from 'react';
import { Check } from 'lucide-react';
import { cn } from '../../utils/cn';

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: React.ReactNode;
  description?: React.ReactNode;
}

/** 20px custom box (native input is visually hidden but stays operable/focusable). */
export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, description, className, id, ...rest }, ref) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    return (
      <label htmlFor={inputId} className={cn('flex items-start gap-3 cursor-pointer', className)}>
        <span className="relative mt-0.5 inline-flex h-5 w-5 shrink-0">
          <input ref={ref} id={inputId} type="checkbox" className="peer absolute inset-0 h-5 w-5 cursor-pointer opacity-0" {...rest} />
          <span
            className={cn(
              'pointer-events-none absolute inset-0 rounded border border-line-strong bg-card',
              'peer-checked:border-brand peer-checked:bg-brand',
              'peer-focus-visible:ring-2 peer-focus-visible:ring-brand peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-page'
            )}
          />
          <Check
            className="pointer-events-none absolute inset-0 m-auto h-3.5 w-3.5 text-on-brand opacity-0 peer-checked:opacity-100"
            aria-hidden="true"
          />
        </span>
        <span className="flex flex-col">
          <span className="text-sm text-ink">{label}</span>
          {description && <span className="text-sm text-muted">{description}</span>}
        </span>
      </label>
    );
  }
);
Checkbox.displayName = 'Checkbox';
